/**
 * RemediationWorkspaceView — Guided Remediation Cockpit
 *
 * 3-pane layout:
 *   Left   — COBOL viewer (read-only), highlights relevant lines for the finding
 *   Right  — Java editor (editable textarea), shows generated Java
 *   Bottom — Fix panel: finding summary + action buttons
 *
 * Prototype actions (all placeholder until real API is wired):
 *   - Apply Recommended Fix → inserts patch snippet into Java editor
 *   - Mark TODO / Stub      → replaces method body with stub comment
 *   - Ask AI for Suggestion → shows placeholder modal / toast
 *   - Re-run Check          → toast "Would run check" (no real API call)
 *   - Resume Pipeline       → enabled only after Re-run produces PASSED (mock)
 *
 * Data source: gateReportService.fetchSourceContext (returns null — editor loads files directly).
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  ArrowLeft, Wrench, Clock, Zap, Play, CheckCircle, Loader2,
  BookOpen, MessageSquare, ShieldCheck
} from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'
import { fetchSourceContext } from '../../services/gates/gateReportService'
import './RemediationWorkspaceView.css'

// ─────────────────────────────────────────────────────────────────────────────
// Stub patch snippets (MOCK_UI_ONLY — replace with real patch API)
// ─────────────────────────────────────────────────────────────────────────────

// MOCK_UI_ONLY
function getMockPatch(findingId, currentJava) {
  const patches = {
    'f-s1': (src) => src.replace(
      /if\s*\(non-""-found\)/g,
      'if (nonSpaceFound)'
    ),
    'f-c1': (src) => src.replace(
      /if\s*\(non-""-found\)/g,
      'if (nonSpaceFound)'
    ),
    'f-c5': (src) => src.replace(
      /if\s*\(non-""-found\)/g,
      'if (nonSpaceFound)'
    ),
    'f-s3': (src) => src.replace(
      /\bloop\s*-\s*1\b/g,
      'loop.intValue() - 1'
    ),
    'f-c2': (src) => src.replace(
      /sizeOutAddr\s*-\s*1/g,
      'sizeOutAddr.intValue() - 1'
    ),
    'f-c4': (src) => src.replace(
      /\bloop\s*-\s*1\b/g,
      'loop.intValue() - 1'
    ),
    'f-c3': (src) => src.replace(
      /cmlhrk00record/g,
      'cmlhrk00Record'
    ),
  }
  const fn = patches[findingId]
  return fn ? fn(currentJava) : currentJava + '\n// TODO: apply fix manually'
}

// MOCK_UI_ONLY — stub template
function getStubTemplate(finding) {
  const loc = finding.javaLocation
  const method = loc ? `at ${loc.file}:${loc.line}` : 'in generated code'
  return `\n    // TODO: ${finding.summary}\n    // COBOL: ${finding.cobolLocation?.file || ''}:${finding.cobolLocation?.line || ''}\n    // Manual resolution required — see Remediation Report for guidance.\n    throw new UnsupportedOperationException("TODO stub — ${method}");\n`
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function RemediationWorkspaceView({ analysisContext, finding, onBack, onFindingResolved }) {
  const gateType  = analysisContext?.gateType || 'structural'
  const jobId     = analysisContext?.jobId || 'mock-job'
  const apiBase   = getAPIBaseUrl()

  const [cobolSource, setCobolSource] = useState('')
  const [javaSource, setJavaSource]   = useState('')
  const [loading, setLoading]         = useState(true)

  // Action states
  const [toast, setToast]             = useState(null) // { message, type }
  const [rerunDone, setRerunDone]     = useState(false)  // enables Resume Pipeline
  const [rerunning, setRerunning]     = useState(false)

  const toastTimerRef = useRef(null)

  const showToast = useCallback((message) => {
    setToast(message)
    clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 3000)
  }, [])

  // Load source context
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchSourceContext(finding.id, jobId, gateType, apiBase)
      .then(ctx => {
        if (cancelled) return
        setCobolSource(ctx.cobolFull || finding.cobolSnippet || '// No COBOL source available')
        setJavaSource(ctx.javaFull || finding.javaSnippet || '// No Java source available')
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setCobolSource(finding.cobolSnippet || '// No COBOL source available')
        setJavaSource(finding.javaSnippet || '// No Java source available')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [finding.id, jobId, gateType, apiBase, finding.cobolSnippet, finding.javaSnippet])

  // ── Action handlers (all prototype) ──────────────────────────────────────

  const handleApplyFix = useCallback(() => {
    // MOCK_UI_ONLY — apply inline patch to editor content
    const patched = getMockPatch(finding.id, javaSource)
    setJavaSource(patched)
    showToast('✓ Recommended fix applied to editor — click Re-run Check to verify')
  }, [finding.id, javaSource, showToast])

  const handleMarkTodo = useCallback(() => {
    // MOCK_UI_ONLY — insert stub template
    const stub = getStubTemplate(finding)
    setJavaSource(prev => prev + stub)
    showToast('TODO stub inserted — click Re-run Check to verify')
    if (onFindingResolved) onFindingResolved(finding.id, 'todo')
  }, [finding, showToast, onFindingResolved])

  const handleAskAI = useCallback(() => {
    // MOCK_UI_ONLY — placeholder, no real AI call
    showToast('AI Suggestion: Use nonSpaceFound (boolean) instead of non-""-found — (mock response)')
  }, [showToast])

  const handleRerunCheck = useCallback(async () => {
    // MOCK_UI_ONLY — simulates a re-run delay then shows PASSED
    setRerunning(true)
    showToast('Running check…')
    await new Promise(r => setTimeout(r, 1800)) // MOCK_UI_ONLY
    setRerunning(false)
    setRerunDone(true)
    showToast('✓ Check passed — Resume Pipeline is now enabled (mock)')
  }, [showToast])

  const handleResumePipeline = useCallback(() => {
    // MOCK_UI_ONLY — placeholder: in production this would trigger the next workflow node
    showToast('Resuming pipeline… (mock — real resume not yet wired)')
    if (onFindingResolved) onFindingResolved(finding.id, 'applied')
  }, [finding.id, showToast, onFindingResolved])

  // ── Render ────────────────────────────────────────────────────────────────

  const GateIcon = gateType === 'compile' ? Zap : ShieldCheck
  const cobolLoc = finding.cobolLocation
    ? `${finding.cobolLocation.file}:${finding.cobolLocation.line}`
    : ''
  const javaLoc = finding.javaLocation
    ? `${finding.javaLocation.file}:${finding.javaLocation.line}`
    : ''

  return (
    <div className="remediation-workspace" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="remediation-header">
        <button className="remediation-back-btn" onClick={onBack}>
          <ArrowLeft size={14} />
          Back to Report
        </button>
        <div className="remediation-title-group">
          <h3 className="remediation-title">
            Fix &amp; Continue
          </h3>
          <div className="remediation-subtitle">
            <GateIcon size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Finding: {finding.summary}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="remediation-loading">
          <Loader2 size={22} className="spin" />
          Loading source context…
        </div>
      ) : (
        <div className="remediation-main">
          {/* 2-pane: COBOL (left) + Java (right) */}
          <div className="remediation-panes">
            {/* COBOL pane — read-only */}
            <div className="cobol-pane">
              <div className="pane-header">
                <span className="pane-badge cobol">COBOL</span>
                Source
                <span className="pane-badge readonly">Read-only</span>
                {cobolLoc && <span className="pane-location">{cobolLoc}</span>}
              </div>
              <div className="cobol-source">
                <pre>{cobolSource}</pre>
              </div>
            </div>

            {/* Java pane — editable */}
            <div className="java-pane">
              <div className="pane-header">
                <span className="pane-badge java">Java</span>
                Generated Output — Editable
                {javaLoc && <span className="pane-location">{javaLoc}</span>}
              </div>
              <div className="java-editor-area">
                <textarea
                  className="java-editor"
                  value={javaSource}
                  onChange={e => setJavaSource(e.target.value)}
                  spellCheck={false}
                />
              </div>
            </div>
          </div>

          {/* Fix panel */}
          <div className="fix-panel">
            <div className="fix-panel-header">
              <Wrench size={13} />
              Fix Panel
            </div>
            <div className="fix-panel-body">
              {/* Finding details */}
              <div className="fix-panel-finding">
                <div className="fix-finding-title">{finding.summary}</div>
                <div className="fix-finding-recommendation">
                  {finding.recommendation || 'No recommendation available.'}
                </div>
              </div>

              {/* Actions */}
              <div className="fix-panel-actions">
                {finding.fixMode === 'allowlisted_auto_fix' && (
                  <button
                    className="fix-action-btn primary"
                    onClick={handleApplyFix}
                    title="Apply a deterministic, allowlisted fix to the Java editor"
                  >
                    <Wrench size={14} />
                    Apply Recommended Fix
                  </button>
                )}

                <button
                  className="fix-action-btn"
                  onClick={handleMarkTodo}
                  title="Insert a TODO stub — explicit user decision only"
                >
                  <Clock size={14} />
                  Mark TODO / Stub
                </button>

                <button
                  className="fix-action-btn"
                  onClick={handleAskAI}
                  title="Get an AI-suggested fix (diff-based, user approved)"
                >
                  <MessageSquare size={14} />
                  Ask AI for Suggestion
                </button>

                <button
                  className="fix-action-btn"
                  onClick={handleRerunCheck}
                  disabled={rerunning}
                  title="Re-run the gate check to verify your changes"
                >
                  {rerunning
                    ? <Loader2 size={14} className="spin" />
                    : <Play size={14} />
                  }
                  Re-run Check
                </button>

                <button
                  className={`fix-action-btn ${rerunDone ? 'success' : ''}`}
                  onClick={handleResumePipeline}
                  disabled={!rerunDone}
                  title={rerunDone ? 'Resume the pipeline' : 'Re-run Check must pass first'}
                >
                  <CheckCircle size={14} />
                  Resume Pipeline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="remediation-toast">{toast}</div>
      )}
    </div>
  )
}
